/**
 * EQ Processor for hearing test equalization calculations
 */

import { TestResult, TestResults, EQSetting, EQProcessorOptions, EqualLoudnessContour, AppConfig } from '../types/index';
import { interpAt, computeQForFrequencies } from './math-utils';
import { Logger } from './logger';

const DEBUG = false;

class EQProcessor {
    isoContourOffset: number;

    constructor(isoContourOffset: number = 90) {
        this.isoContourOffset = isoContourOffset;
    }

    /**
     * Pure function that computes EQ settings from measured results and a contour.
     * @param {Array} results - Array of {frequency, db}
     * @param {Array} contour - Array of [frequency, value] where value includes ISO offset
     * @param {Object} options - {minFreq, maxFreq, isoOffset, gainStrength, strength}
     * @returns {Array|null} Array of {frequency, gain, q} or null if no results
     */
    computeEqSettings(results: TestResult[], contour: EqualLoudnessContour[], options: EQProcessorOptions = {}): EQSetting[] | null {
        const isoOffset = options.isoOffset !== undefined ? options.isoOffset : this.isoContourOffset;
        const minF = options.minFreq !== undefined ? options.minFreq : 20;
        const maxF = options.maxFreq !== undefined ? options.maxFreq : 20000;
        // Backwards compatible: prefer baseGainStrength, fall back to legacy gainStrength option
        const baseGainStrength = (options.baseGainStrength !== undefined) ? options.baseGainStrength : (options.gainStrength !== undefined ? options.gainStrength : 0.6);
        // Strength setting: if absent, behave as exponent 1 (preserve previous behaviour)
        const strengthSetting = (options.strength !== undefined) ? options.strength : null;
        const strengthExponent = (strengthSetting === null) ? 1.0 : Math.pow(1.25, (strengthSetting - 3));

        // Debug logging removed/gated — keep function deterministic and silent by default.

        if (!results || results.length === 0 || !contour || contour.length === 0) return null;

        // 1. Calculate diffs = measured_db - (contourDb - isoOffset)
        const diffs = results.map((result: TestResult) => {
            let contourDb = 0;
            // Interpolate the contour linearly in frequency, matching Peace
            // (Peace16911.au3:6355-6356): C[k-1] + (C[k]-C[k-1])*(f-F[k-1])/(F[k]-F[k-1]).
            if (contour && contour.length > 0) {
                // find bracketing points
                let p1: number[] | undefined;
                let p2: number[] | undefined;
                for (let i = 0; i < contour.length - 1; i++) {
                    if (result.frequency >= contour[i][0] && result.frequency <= contour[i + 1][0]) {
                        p1 = contour[i] as number[];
                        p2 = contour[i + 1] as number[];
                        break;
                    }
                }
                if (p1 && p2) {
                    contourDb = interpAt(result.frequency, p1 as any, p2 as any, 'linear', 'linear');
                } else {
                    // outside range: clamp to ends
                    if (result.frequency < contour[0][0]) contourDb = contour[0][1];
                    else contourDb = contour[contour.length - 1][1];
                }
            }
            contourDb -= isoOffset;
            
            const diff = result.db - contourDb;
            return { frequency: result.frequency, db: diff };
        });

        // diffs calculated

        // 2. Filter diffs inside normalization window
        const inWindow = diffs.filter((d: {frequency: number, db: number}) => d.frequency >= minF && d.frequency <= maxF);

        // 3. Compute average diff using in-window points; if empty, fallback to all points
        const sourceForAvg = (inWindow.length > 0) ? inWindow : diffs;
        const avgDiff = sourceForAvg.reduce((s: number, v: {frequency: number, db: number}) => s + v.db, 0) / sourceForAvg.length;

        // average diff computed

        // 4. Subtract avgDiff from all diffs (normalization)
        diffs.forEach((d: {frequency: number, db: number}) => d.db -= avgDiff);

        // diffs normalized

        // Ensure diffs are sorted by frequency for Q calculation
        diffs.sort((a: {frequency: number, db: number}, b: {frequency: number, db: number}) => a.frequency - b.frequency);

        // 5. Compute per-band Q using canonical helper (Peace peak-quality index).
        // Peace computes Q over the full slider grid (all preset frequencies, tested
        // or not), so when a grid is supplied via options.qFrequencies use it and map
        // each tested band to its grid entry; otherwise fall back to tested-only.
        const DEFAULT_Q = 1.41;
        const peakQuality = options.peakQuality !== undefined ? options.peakQuality : 0;
        const freqs = diffs.map((d: {frequency: number, db: number}) => d.frequency);
        let qs: number[];
        const grid = options.qFrequencies;
        if (Array.isArray(grid) && grid.length > 1) {
            const sortedGrid = grid.slice().sort((a, b) => a - b);
            const gridQs = computeQForFrequencies(sortedGrid, peakQuality);
            qs = freqs.map(f => {
                const gi = sortedGrid.findIndex(g => Math.abs(g - f) < 1);
                return gi >= 0 ? gridQs[gi] : DEFAULT_Q;
            });
        } else {
            qs = computeQForFrequencies(freqs, peakQuality);
        }

        // 6. Build eq settings applying base gain strength and optional strength exponent
        const eqSettings = diffs.map((d: {frequency: number, db: number}, idx: number): EQSetting => ({
            frequency: d.frequency,
            gain: d.db * baseGainStrength * strengthExponent,
            q: qs[idx] !== undefined ? qs[idx] : DEFAULT_Q
        }));

        // final eq settings computed

        return eqSettings;
    }

    /**
     * Generate EQ settings from test results
     * @param {Object} testResults - Test results object
     * @param {Object} equalLoudnessContours - Contour data
     * @param {string} earSelection - 'both', 'left', or 'right'
     * @param {string} contourName - Selected contour name
     * @param {Object} config - App configuration
     * @returns {Array|null} EQ settings or null
     */
    generateEqSettings(testResults: TestResults, equalLoudnessContours: { [key: string]: EqualLoudnessContour[] }, earSelection: 'left' | 'right' | 'both', contourName: string, config: AppConfig): EQSetting[] | null {
        const results = testResults[earSelection];
        
        if (DEBUG) Logger.debug('=== EQ PROCESSOR DEBUG ===');
        if (DEBUG) Logger.debug('Input results:', results);
        if (DEBUG) Logger.debug('Ear selection:', earSelection);
        if (DEBUG) Logger.debug('Contour name:', contourName);
        if (DEBUG) Logger.debug('Available contours:', Object.keys(equalLoudnessContours || {}));
        
        if (!results || results.length === 0 || contourName === 'none') {
            if (DEBUG) Logger.debug('No results or contour is none');
            return null;
        }

        const contour = equalLoudnessContours[contourName];
        if (!contour) {
            if (DEBUG) Logger.debug('Contour not found:', contourName);
            return null;
        }

        if (DEBUG) Logger.debug('Using contour:', contour ? `${contour.length} points` : 'null');
        if (DEBUG) Logger.debug('Config:', config);

        // Use the pure computeEqSettings function and pass current config window.
        // minFreq/maxFreq read the fields the config actually persists (minFreq/maxFreq);
        // strength applies Peace's 1.25^(strength-3) usage-strength factor.
        const eqSettings = this.computeEqSettings(results, contour, {
            minFreq: config.minFreq,
            maxFreq: config.maxFreq,
            isoOffset: this.isoContourOffset,
            gainStrength: 0.6,
            strength: config.strength,
            peakQuality: config.peakQuality,
            qFrequencies: config.presetFrequencies
        });

        if (DEBUG) Logger.debug('Computed EQ settings:', eqSettings);
        return eqSettings;
    }

    /**
     * Interpolate gain from eqSettings for any frequency
     * @param {number} frequency - Target frequency
     * @param {Array} eqSettings - EQ settings array
     * @returns {number} Interpolated gain
     */
    getFinalInterpolatedGain(frequency: number, eqSettings: EQSetting[]): number {
        if (!eqSettings || eqSettings.length === 0) return 0;

        // work on a copy to avoid mutating caller
        const settings = eqSettings.slice().sort((a: EQSetting, b: EQSetting) => a.frequency - b.frequency);

        let lowerPoint: EQSetting | null = null;
        let upperPoint: EQSetting | null = null;

        for (let i = 0; i < settings.length; i++) {
            if (settings[i].frequency <= frequency) lowerPoint = settings[i];
            if (settings[i].frequency >= frequency && !upperPoint) upperPoint = settings[i];
        }

        if (lowerPoint && upperPoint) {
            if (lowerPoint.frequency === upperPoint.frequency) return lowerPoint.gain;
            return interpAt(frequency, [lowerPoint.frequency, lowerPoint.gain] as any, [upperPoint.frequency, upperPoint.gain] as any, 'log', 'log');
        } else if (lowerPoint) {
            return lowerPoint.gain;
        } else if (upperPoint) {
            return upperPoint.gain;
        }
        return 0;
    }

    /**
     * Interpolate Q from eqSettings for any frequency
     * @param {number} frequency - Target frequency
     * @param {Array} eqSettings - EQ settings array
     * @returns {number} Interpolated Q value
     */
    getFinalInterpolatedQ(frequency: number, eqSettings: EQSetting[]): number {
        const DEFAULT_Q = 1.41;
        if (!eqSettings || eqSettings.length === 0) return DEFAULT_Q;

        // work on a copy to avoid mutating caller
        const settings = eqSettings.slice().sort((a: EQSetting, b: EQSetting) => a.frequency - b.frequency);

        let lowerPoint: EQSetting | null = null;
        let upperPoint: EQSetting | null = null;

        for (let i = 0; i < settings.length; i++) {
            if (settings[i].frequency <= frequency) lowerPoint = settings[i];
            if (settings[i].frequency >= frequency && !upperPoint) upperPoint = settings[i];
        }

        if (lowerPoint && upperPoint) {
            if (lowerPoint.frequency === upperPoint.frequency) return lowerPoint.q;
            return interpAt(frequency, [lowerPoint.frequency, lowerPoint.q ?? 1.41] as any, [upperPoint.frequency, upperPoint.q ?? 1.41] as any, 'log', 'log');
        } else if (lowerPoint) {
            return lowerPoint.q;
        } else if (upperPoint) {
            return upperPoint.q;
        }
        return DEFAULT_Q;
    }
}

// ES module export
export { EQProcessor };
export default EQProcessor;
